import React, {useEffect, useState} from "react";
import style from "../../assets/scss/sub/checkupInfo.module.scss";
import addIcon from "../../assets/images/button/icon/addIcon.svg";
import {useLanguage, useTimeConverter} from "../../hooks/hooks";
import AddCheckupInfoModal from "../common/modal/AddCheckupInfoModal";
import {useTranslation} from "react-i18next";
import {useCheckupInfoFunctions} from "./hooks/useCheckupInfoFunctions";
import {useMyPageFunctions} from "./mypage/useMyPageFunctions";

interface ProfileInfo {
  idx: number;
  name: string;
  birthday: string;
  createAt: string;
  userCode: string;
  testCnt: number;
  isOwnerProfile: boolean;
}

interface ProfileInfoProps {
  profileIdx: number;
  profileInfo: ProfileInfo;
}

export function CheckupInfo({ profileIdx, profileInfo }: ProfileInfoProps) {
  const { t } = useTranslation("checkupInfo");
  const { isKorean } = useLanguage();
  const { timeConverter } = useTimeConverter();
  const [paginatedData, setPaginatedData] = useState<any[]>([]);
  const [isCheckupInfoModalOpen, setIsCheckupInfoModalOpen] = useState(false);
  const itemsPerPage = 4;
  const {CommonPageField} = useMyPageFunctions();

  const {
    handleGuideVisible,
    handleOpenTestResult,
    handleOpenGuide,
    getProfileInfo,
    profileData,
    pageIdx,
    handleBrainType,
    setPageIdx,
    getAuthToken,
    accessToken
  } = useCheckupInfoFunctions();

  const toggleAddCheckupInfoModal = () => {
    setIsCheckupInfoModalOpen((prev) => !prev);
  };

  useEffect(() => {
    if (profileIdx) getProfileInfo({ profileIdx });
  }, [profileIdx, getProfileInfo, isCheckupInfoModalOpen]);

  useEffect(() => {
    if (profileIdx) getAuthToken({ userProfileIdx: profileIdx });
  }, [getAuthToken, profileIdx]);

  useEffect(() => {
    if (accessToken) {
      // Access Token이 설정된 이후에 필요한 작업을 수행할 수 있습니다.
    }
  }, [accessToken]);

  useEffect(() => {
    if (isCheckupInfoModalOpen) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [isCheckupInfoModalOpen]);

  useEffect(() => {
    const startIdx = pageIdx * itemsPerPage;
    const endIdx = startIdx + itemsPerPage;
    setPaginatedData(profileData.slice(startIdx, endIdx));
  }, [profileData, pageIdx]);

  const totalPages = Math.max(Math.ceil(profileData.length / itemsPerPage), 1); // 최소 1페이지

  const handlePageChange = (newPageIdx: number) => {
    if (newPageIdx >= 0 && newPageIdx < totalPages) {
      setPageIdx(newPageIdx);
    }
  };

  return (
      <div className={style.container}>
        <div className={style.containerContentBox}>
          <h2 className={style.containerTitle}>
            {t('title')}&nbsp; <span className={style.profileCount}>
          ({profileInfo?.name ?? "나"}
        </span>
            <span className={style.profileCount}>)</span>
          </h2>
          <button
              className={`${style.addProfileButton} ${style.addCheckupInfoButton} ${isKorean ? "" : style.en}`}
              onClick={toggleAddCheckupInfoModal}
          >
            <img src={addIcon} className={style.addIcon} alt="icon" width={26} height={26} />
            {t('btn.addOrgan')}
          </button>
        </div>
        <div>
          <div className={style.labelContainer}>
            <div className={style.firstLabel}>{t('detail.column.testType')}</div>
            <div className={style.secondLabel}>{t('detail.column.brainType')}</div>
            <div className={style.thirdLabel}>{t('detail.column.completedAt')}</div>
            <div className={style.fourthLabel}>{t('detail.column.testResult')}</div>
          </div>
          <div className={style.resultInfoContainer}>
            {paginatedData.length > 0 ? (
                paginatedData.map((item) => (
                    <div key={item.contentExecutedInfoIdx} className={style.resultInfoData}>
                      <div className={style.firstInfoData}>
                        {item.unitVoucherType === "KPASS" ? "K-PASS" : "D-CAS"}
                        <br />
                        {item.isLinkedOrganTest
                            ? t('detail.row.organTest')
                            : !item.customerType
                                ? item.testRound + t('detail.row.testRound')
                                : ""}
                      </div>
                      <div className={style.secondInfoData}>
                        {handleBrainType(item.brainType)}
                      </div>
                      <div className={style.thirdInfoData}>
                        {timeConverter.convertToFormattedDate(timeConverter.convertToLocalDate(item.endedAt).split(" ")[0])}
                      </div>
                      <div className={style.fourthInfoData}>
                        <button
                            className={style.resultButton}
                            onClick={() =>
                                handleOpenTestResult(
                                    item.contentExecutedInfoIdx,
                                    item.unitVoucherType,
                                    item.kpassResultForm,
                                    item.dcasResultForm
                                )
                            }
                        >
                          {t('detail.row.testHistory')}
                        </button>
                        {handleGuideVisible(item.unitVoucherType, item.kpassResultForm, item.dcasResultForm) && (
                            <button
                                className={style.guideButton}
                                onClick={() => handleOpenGuide(item.contentExecutedInfoIdx, item.unitVoucherType)}
                            >
                              {t('detail.row.testGuide')}
                            </button>
                        )}
                      </div>
                    </div>
                ))
            ) : (
                <div className={style.noPaymentInfo}>{t('detail.row.emptyTestHistory')}</div>
            )}
          </div>

          <CommonPageField handlePageChange={handlePageChange} currentPage={pageIdx} totalPages={totalPages} />
        </div>
        {profileIdx !== null && (
            <AddCheckupInfoModal
                isOpen={isCheckupInfoModalOpen}
                onClose={toggleAddCheckupInfoModal}
                profileIdx={profileIdx}
            />
        )}
      </div>
  );
}
